<template>
  <div class="page-wrapper">
    <h2 class="tw-mb-6">Übersicht - Alle Abonnements</h2>

    <div class="tw-w-full tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-2 md:tw-gap-6 tw-mb-6" style="max-width: 872px;">
      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Suchen
        </label>
        <form class="tw-relative" @submit.prevent="fetchDocuments()">
          <input
            type="text"
            v-model="searchParams.keyword"
            class="fox-input--simple fox-input--simple-search tw-w-full tw-bg-white"
          >

          <div class="tw-absolute tw-inset-y-0 tw-right-2 tw-flex tw-items-center">
            <v-icon class="tw-cursor-pointer" @click="fetchDocuments()">mdi-magnify</v-icon>
          </div>
        </form>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Kunde
        </label>
        <div class="tw-relative">
          <select v-model="searchParams.clientUuid" @change="fetchDocuments()" class="fox-input--simple fox-input--simple-select tw-w-full tw-bg-white tw-capitalize">
            <option selected :value="null">Kunde auswählen</option>
            <option v-for="client in clients" :key="client.uuid" :value="client.uuid">
              {{ client.name }}
            </option>
          </select>

          <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
            <v-icon>mdi-menu-down</v-icon>
          </div>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Zeitraum
        </label>
        <date-picker
          v-model:value="searchParams.date"
          @clear="clearDate"
          @change="fetchDocuments()"
          type="date"
          format="DD.MM.YYYY"
          value-type="YYYY-MM-DD"
        />
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Status
        </label>
        <div class="tw-relative">
          <select v-model="searchParams.status" @change="fetchDocuments()" class="fox-input--simple fox-input--simple-select tw-w-full tw-bg-white tw-capitalize">
            <option selected :value="null">Status auswählen</option>
            <option v-for="status in statusFilters" :key="status.value" :value="status.value">
              {{ status.text }}
            </option>
          </select>

          <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
            <v-icon>mdi-menu-down</v-icon>
          </div>
        </div>
      </div>
    </div>

    <div class="table-block">
      <div class="tw-flex tw-items-start tw-gap-6 tw-mb-6">
        <div class="tw-flex-1 tw-flex tw-flex-col">
          <h3 class="tw-text-xl tw-font-bold tw-mb-3">Alle wiederkehrenden Rechnungen</h3>

          <div class="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1">
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="selectAllInvoices">alle auswählen</span>

            <span class="text-link" v-if="selectedInvoicesUuids.length > 0">{{ selectedInvoicesUuids.length }} ausgewählt</span>

            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('PAID')">als bezahlt markieren</span>
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('OPEN')">als offen markieren</span>
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('CANCELED')">stornieren</span>
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('OVERDUE')">löschen</span>
          </div>
        </div>

        <button
          v-if="selectedInvoicesUuids.length > 0"
          @click="exportPdf"
          class="fox-invoice-action__create-btn fox-invoice-action__create-btn--purple fox-invoice-action__create-btn--purple--small">
          <DownloadIcon />
          <span class="tw-text-black">Export - PDF</span>
        </button>
      </div>

      <div class="app-table-wrapper">
        <table class="app-table">
          <thead>
            <tr>
              <th>RECHNUNG / Kunde</th>
              <th>Datei</th>
              <th>Nächste RG AM</th>
              <th>ZAHLUNGSFRIST</th>
              <th>TURNUS</th>
              <th>Status</th>
              <th>Preis (Netto)</th>
              <th>Ust.</th>
              <th>PREIS (BRUTTO)</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="invoice in invoices" :key="invoice.uuid">
              <td>
                <div class="tw-flex tw-items-center tw-gap-6" style="min-height: 44px;">
                  <label class="custom-checkbox">
                    <input
                      type="checkbox"
                      v-model="selectedInvoicesUuids"
                      :value="invoice.uuid"
                    />
                    <span class="checkmark"></span>
                  </label>

                  <div class="tw-flex tw-flex-col tw-text-left">
                    <span class="tw-inline-block tw-font-medium tw-truncate" style="max-width: 180px;">{{ invoice.client?.name || '' }}</span>
                    <span class="tw-inline-block tw-text-gray-500 tw-truncate" style="max-width: 180px;">{{ invoice.client?.company || '' }}</span>
                  </div>
                </div>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.fileType }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.createdAt }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.deadlineDate }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap tw-text-green-500">{{ invoice.subscription?.text || '' }}</span>
              </td>

              <td>
                <div class="tw-relative">
                  <select
                    class="app-table-select tw-capitalize"
                    :class="{
                      'app-table-select--open': invoice.status.value === 'OPEN',
                      'app-table-select--active': invoice.status.value === 'PAID',
                      'app-table-select--error': invoice.status.value === 'OVERDUE'
                    }"
                    :value="invoice.status.value"
                    @change="updateInvoiceStatus($event.target.value, invoice.uuid)"
                  >
                    <option v-for="status in statusFilters" :key="status.value" :value="status.value" :selected="invoice.status.value === status.value">
                      {{ status.text }}
                    </option>
                  </select>

                  <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
                    <v-icon>mdi-menu-down</v-icon>
                  </div>
                </div>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.netto }} {{ invoice.unit }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.tax }} {{ invoice.unit }}</span>
              </td>

              <td>
                <span class="tw-font-bold tw-whitespace-nowrap">{{ invoice.brutto }} {{ invoice.unit }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="tw-mt-8 tw-mb-4">
        <AppPagination
          v-if="totalCount > 0"
          :count="totalCount"
          :currentPage="currentPage"
          @update="(page) => fetchDocuments(page)"
        />
      </div>
    </div>

    <v-snackbar
      v-model="showMessage"
      location="top"
    >
      Datei nicht gefunden
      <template #actions>
        <v-btn
          color="error"
          @click="showMessage = false"
        >
          Schließen
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ApiService from '@/services/api.service'
import JSZip from 'jszip'
import DatePicker from 'vue-datepicker-next'
import AppPagination from '@/components/Common/AppPagination.vue'
import DownloadIcon from '@/components/Icon/DownloadIcon.vue'

export default {
  name: 'AllRecurringInvoices',
  components: {
    DatePicker,
    AppPagination,
    DownloadIcon
  },
  data () {
    return {
      clients: null,
      statusFilters: null,
      searchParams: {
        keyword: '',
        status: null,
        clientUuid: null,
        date: null
      },
      currentPage: 1,
      totalCount: 0,
      invoices: [],
      selectedInvoicesUuids: [],
      showMessage: false
    }
  },
  mounted () {
    this.fetchUserStatus()
    this.fetchClients()
    this.fetchStatusFilters()
    this.fetchDocuments()
  },
  methods: {
    async fetchUserStatus () {
      try {
        await ApiService.get('/user/status')
      } catch (error) {
        this.$router.push({ name: 'Login' })
      }
    },
    async fetchClients () {
      try {
        const response = await ApiService.get('/client/search', {
          params: {
            limit: -1
          }
        })
        this.clients = response.data.data
      } catch (error) {
        console.error('Error fetching clients:', error)
      }
    },
    async fetchStatusFilters () {
      try {
        const response = await ApiService.get('/file/status')
        this.statusFilters = response.data
      } catch (error) {
        console.error('Error fetching filters:', error)
      }
    },
    async fetchDocuments (page) {
      if (page) {
        this.currentPage = page
      }
      try {
        const response = await ApiService.get('/invoice/search', {
          params: {
            type: 'recurring',
            page: this.currentPage,
            keyword: this.searchParams.keyword,
            status: this.searchParams.status,
            clientUuid: this.searchParams.clientUuid,
            date: this.searchParams.date
          }
        })
        this.invoices = response.data.data
        this.totalCount = response.data.pagination.total
      } catch (error) {
        console.error('Error fetching clients:', error)
      }
    },
    clearDate () {
      this.searchParams.date = ''
    },
    async updateInvoiceStatus (status, uuids = null) {
      try {
        const uuidArray = uuids
          ? (Array.isArray(uuids) ? uuids : [uuids])
          : this.selectedInvoicesUuids

        const payload = uuidArray.map(uuid => ({
          uuid,
          status
        }))

        if (!payload.length) return

        await ApiService.post('/invoice/status/update', payload)
        this.fetchDocuments()
      } catch (error) {
        console.error('Error updating invoice status:', error)
      }
    },
    selectAllInvoices () {
      const currentPageUuids = this.invoices.map(invoice => invoice.uuid)
      this.selectedInvoicesUuids = Array.from(new Set([...this.selectedInvoicesUuids, ...currentPageUuids]))
    },
    async exportPdf () {
      const zip = new JSZip()
      let hasFiles = false

      const addFilesToZip = (items) => {
        items.forEach(item => {
          if (item.fileBase64) {
            hasFiles = true
            zip.file(`${item.uuid}.pdf`, item.fileBase64, { base64: true })
          }
        })
      }

      addFilesToZip(this.selectedItems)

      if (!hasFiles) {
        this.showMessage = true
        return
      }

      const content = await zip.generateAsync({ type: 'blob' })

      const url = URL.createObjectURL(content)
      const a = document.createElement('a')
      a.href = url
      a.download = 'invoices.zip'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  },
  computed: {
    selectedItems () {
      return this.invoices.filter(invoice => this.selectedInvoicesUuids.includes(invoice.uuid))
    }
  }
}
</script>
